/**
 * Real-weather state: the fetched real-world wind FIELD for the barb overlay and
 * the auto-router, used on real-area (Antares) charts. We keep the whole hourly
 * field (every grid point, every forecast hour) so changing the forecast hour is
 * instant — we re-derive the barb snapshot from the cached field rather than
 * refetching. The fetch is keyed by model + rounded extent only (NOT the hour),
 * so scrubbing the forecast time costs nothing. The fetch lives in
 * src/weather/realWeather.ts.
 */
import { create } from 'zustand';
import type { WindSample } from '@/weather';
import type { LonLatBounds } from '@/store/chartSources';
import { fetchConsensusField } from '@/weather/consensusField';
import {
  fetchRealWindField,
  fieldSamplesAt,
  type RealModelId,
  type RealWindField,
} from '@/weather/realWeather';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface RealWeatherState {
  /** The whole cached field (all points, all hours) — null until first load. */
  field: RealWindField | null;
  /** The barb snapshot derived from `field` at the current target time. */
  samples: WindSample[];
  status: Status;
  error: string | null;
  /** model + rounded-extent key of the cached field, so we don't refetch the same view. */
  key: string | null;
  /**
   * Ensure the field for this view+model is loaded, then derive the barb samples
   * at `targetMs`. If the field is already cached for this view+model, no fetch
   * happens — we just re-slice the cached field at the new time (instant).
   */
  load: (bounds: LonLatBounds, modelId: RealModelId, targetMs: number, days?: number) => Promise<void>;
  clear: () => void;
}

/**
 * The extent a fetched field actually covers. The wind field carries its grid
 * points rather than a bounds box, so the box is recovered from the corner
 * points, which the fetch laid out over exactly the bounds it was asked for.
 */
function fieldExtent(points: [number, number][]): [number, number, number, number] {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const [lon, lat] of points) {
    if (lon < w) w = lon;
    if (lat < s) s = lat;
    if (lon > e) e = lon;
    if (lat > n) n = lat;
  }
  return [w, s, e, n];
}

/** Does the loaded extent fully cover the asked-for one. */
function containsBounds(
  outer: [number, number, number, number],
  inner: [number, number, number, number],
): boolean {
  return outer[0] <= inner[0] && outer[1] <= inner[1] && outer[2] >= inner[2] && outer[3] >= inner[3];
}

/** Round the extent so small pans reuse the same fetch. Time is NOT in the key. */
function keyFor(bounds: LonLatBounds, modelId: string): string {
  const r = (n: number) => n.toFixed(2);
  return `${modelId}:${r(bounds[0])},${r(bounds[1])},${r(bounds[2])},${r(bounds[3])}`;
}

export const useRealWeatherStore = create<RealWeatherState>((set, get) => ({
  field: null,
  samples: [],
  status: 'idle',
  error: null,
  key: null,

  load: async (bounds, modelId, targetMs, days = 3) => {
    const key = keyFor(bounds, modelId);
    const cur = get();

    // Field already cached for this view+model: just re-slice at the new time.
    // Never for the blend, which holds no field and has to go and ask again.
    if (modelId !== 'consensus' && cur.key === key && cur.field) {
      set({ samples: fieldSamplesAt(cur.field, targetMs), status: 'ready', error: null });
      return;
    }
    // The cached field COVERS the requested extent, even though the key moved:
    // the view has panned or zoomed within what is already loaded (the caller
    // fetches padded bounds for exactly this reason), so there is nothing new
    // to ask the server for. This is what stops a fetch per gesture.
    if (
      modelId !== 'consensus' &&
      cur.field &&
      cur.key?.startsWith(`${modelId}:`) &&
      containsBounds(fieldExtent(cur.field.points), bounds)
    ) {
      set({ samples: fieldSamplesAt(cur.field, targetMs), status: 'ready', error: null });
      return;
    }
    // A fetch for this exact key is already in flight: leave it to finish.
    if (cur.key === key && cur.status === 'loading') return;

    set({ key, status: 'loading', error: null });
    try {
      // The blend is not a model and has no single field behind it: it is several
      // models combined per grid point, so it produces samples directly. There is
      // no cached field to re-slice, which means moving the forecast hour refetches
      // rather than being instant. Worth it for an answer that does not depend on
      // having picked the right model, and the requests are small.
      if (modelId === 'consensus') {
        const { samples, used } = await fetchConsensusField(bounds, targetMs);
        if (get().key !== key) return;
        set({
          field: null,
          samples,
          status: 'ready',
          error: used.length < 2 ? `Only ${used.length} model answered here` : null,
        });
        return;
      }
      const field = await fetchRealWindField(bounds, modelId, undefined, days);
      // Ignore a stale response if the view/model moved on while we waited.
      if (get().key !== key) return;
      set({ field, samples: fieldSamplesAt(field, targetMs), status: 'ready', error: null });
    } catch (e) {
      if (get().key !== key) return;
      const msg = e instanceof Error ? e.message : 'Weather fetch failed';
      // 429 is the server asking us to slow down, not a model with no data, and
      // saying the wrong one sends the skipper hunting through models for a
      // problem that fixes itself in a minute.
      set({
        status: 'error',
        error: msg.includes('429')
          ? 'The weather server asked us to slow down. Give it a minute, then nudge the map.'
          : msg,
      });
    }
  },

  clear: () => set({ field: null, samples: [], status: 'idle', error: null, key: null }),
}));
